import { computed } from 'vue';
import { useStore } from 'vuex';

/**
 * Resolves a resource descriptor from an object with optional id fields.
 * Precedence: Category → CategoryType → Grouping. Returns null when no id is set.
 * @param {{ categoryId?: string|number, categoryTypeId?: string|number, groupingId?: string|number }} ids
 * @returns {{ id: string|number, type: 'Category'|'CategoryType'|'Grouping' } | null}
 */
export function resolveResource({ categoryId, categoryTypeId, groupingId } = {}) {
  if (categoryId) return { id: categoryId, type: 'Category' };
  if (categoryTypeId) return { id: categoryTypeId, type: 'CategoryType' };
  if (groupingId) return { id: groupingId, type: 'Grouping' };
  return null;
}

export function useFlashcardCounts() {
  const store = useStore();

  /** @type {import('vue').ComputedRef<Object<string, {count: number}>>} Map of category ID to raw flashcard count (from product associations, no status breakdown). */
  const flashcardCounts = computed(() => {
    const byCategoryId = store.state.productAssociations.productAssociationsByCategoryId;
    const counts = {};
    for (const categoryId of Object.keys(byCategoryId)) {
      counts[categoryId] = {
        count: byCategoryId[categoryId].length,
      };
    }
    return counts;
  });

  const greenStatusId = computed(() => store.getters['flashcardStatuses/greenFlashcardStatus']?.id);
  const yellowStatusId = computed(() => store.getters['flashcardStatuses/yellowFlashcardStatus']?.id);
  const redStatusId = computed(() => store.getters['flashcardStatuses/redFlashcardStatus']?.id);
  const unansweredStatusId = computed(() => store.getters['flashcardStatuses/unansweredFlashcardStatus']?.id);

  const bookmarkedFlashcardIds = computed(() => {
    const activeBookmarks = store.getters['bookmarks/activeBookmarks'];
    return new Set(
      activeBookmarks
        .filter(b => b.bookmarkableType === 'Flashcard')
        .map(b => b.bookmarkableId),
    );
  });

  function getCountsForCategory(categoryId) {
    const flashcardIds = store.getters['productAssociations/categoryFlashcards'](categoryId);

    const counts = emptyCounts();
    counts.count = flashcardIds.length;

    for (const flashcardId of flashcardIds) {
      const state = resolveFlashcardState(flashcardId, categoryId);
      if (bookmarkedFlashcardIds.value.has(flashcardId)) counts.bookmarked++;

      if (!state || !answered(state)) {
        if (state && state.flashcardStatusId === unansweredStatusId.value) counts.taggedUnanswered++;
        if (state) counts.unansweredWithState++;
        else counts.unansweredNoState++;
        counts.unanswered++;
        continue;
      }

      if (state.flashcardStatusId === greenStatusId.value) counts.green++;
      else if (state.flashcardStatusId === yellowStatusId.value) counts.yellow++;
      else if (state.flashcardStatusId === redStatusId.value) counts.red++;
      else if (state.flashcardStatusId === unansweredStatusId.value) {
        counts.taggedUnanswered++;
        counts.taggedUnansweredButHasAnswer++;
      }

      counts.answered++;
      counts.totalTime += state.timeInSeconds || 0;
      if (state.correct) counts.correct++;
      else counts.incorrect++;
    }

    counts.averageTime = counts.answered > 0 ? counts.totalTime / counts.answered : null;
    return counts;
  }

  function resolveFlashcardState(flashcardId, targetCategoryId) {
    const key = `${flashcardId}-category-${targetCategoryId}`;
    return store.state.flashcardStates.flashcardStates[key] || null;
  }

  /**
   * Returns the number of unique answered flashcards across all categories reachable from a resource.
   * Flashcards appearing in multiple categories are counted only once.
   * For Grouping resources, considers CategoryType asset associations; other
   * association types are ignored.
   * @param {string|number} resourceId - The ID of the resource.
   * @param {string} resourceType - 'Category', 'CategoryType', or 'Grouping'.
   * @returns {number} The deduplicated count of flashcards with a state indicating they've been answered (correct or incorrect).
   */
  function countUniqueAnsweredFlashcards(resourceId, resourceType) {
    const categoryIdSet = new Set(collectCategoryIdsByResource(resourceId, resourceType));
    const flashcardStatesList = store.getters['flashcardStates/flashcardStateList'];
    const uniqueAnsweredFlashcardIds = new Set();

    for (const fs of flashcardStatesList) {
      if (categoryIdSet.has(fs.categoryId) && answered(fs)) {
        uniqueAnsweredFlashcardIds.add(fs.flashcardId);
      }
    }

    return uniqueAnsweredFlashcardIds.size;
  }

  /**
   * Returns answered/correct counts where each unique flashcardId contributes at most once,
   * using the state with the latest mobileCreatedAt across the resource's categories.
   * If a flashcard's most recent state is unanswered, it does not count toward answered.
   * When both resourceId and resourceType are null/undefined, aggregates across every category.
   * @param {string|number|null} resourceId - The ID of the resource, or null for all.
   * @param {string|null} resourceType - 'Category', 'CategoryType', 'Grouping', or null for all.
   * @returns {{ answered: number, correct: number }}
   */
  function getUniqueLatestFlashcardOutcomes(resourceId, resourceType) {
    const categoryIdSet = new Set(collectCategoryIdsByResource(resourceId, resourceType));
    const flashcardStatesList = store.getters['flashcardStates/flashcardStateList'];
    const latestByFlashcardId = new Map();

    for (const fs of flashcardStatesList) {
      if (!categoryIdSet.has(fs.categoryId)) continue;
      const existing = latestByFlashcardId.get(fs.flashcardId);
      if (!existing || (fs.mobileCreatedAt || 0) > (existing.mobileCreatedAt || 0)) {
        latestByFlashcardId.set(fs.flashcardId, fs);
      }
    }

    let answered = 0;
    let correct = 0;
    for (const fs of latestByFlashcardId.values()) {
      if (fs.correct === true) {
        answered++;
        correct++;
      } else if (fs.correct === false) {
        answered++;
      }
    }
    return { answered, correct };
  }

  /**
   * Returns aggregated flashcard status counts across all categories reachable from a resource.
   * For Category resources, recursively sums counts across the category and its descendants.
   * For CategoryType resources, aggregates across all root categories of the type.
   * For Grouping resources, considers CategoryType asset associations; other
   * association types are ignored.
   * When both resourceId and resourceType are null/undefined, aggregates across every category.
   * @param {string|number|null} resourceId - The ID of the resource, or null for all.
   * @param {string|null} resourceType - 'Category', 'CategoryType', 'Grouping', or null for all.
   * @returns {Object} Counts object with keys: count, green, yellow, red, answered, unanswered, correct, incorrect, bookmarked, etc.
   */
  function getFlashcardCounts(resourceId, resourceType) {
    return aggregateCounts(collectCategoryIdsByResource(resourceId, resourceType));
  }

  function aggregateCounts(categoryIds) {
    return categoryIds.reduce(
      (acc, categoryId) => mergeCounts(acc, getCountsForCategory(categoryId)),
      emptyCounts(),
    );
  }

  /**
   * Recursively collects a category and all of its descendant category IDs.
   * Normalizes the root ID to a Number so results match numeric IDs on flashcard states.
   * @param {string|number} categoryId - The root category ID to start from.
   * @returns {Array<number>} Flat array including the given category and all descendants.
   */
  function collectAllCategoryIds(categoryId) {
    const normalizedId = Number(categoryId);
    const category = store.getters['categories/categories'][normalizedId];
    if (!category || category.children.length === 0) return [normalizedId];
    return [normalizedId, ...category.children.flatMap(collectAllCategoryIds)];
  }

  /**
   * Collects all root categoryIds from a category type
   * @param {string|number} categoryTypeId - The category type ID to collect from.
   * @returns {Array<string|number>} Flat array of categoriesIds
   */
  function collectRootCategoryIdsFromType(categoryTypeId) {
    const rootCategoryIds = store.getters['categories/type'][categoryTypeId] || [];
    return rootCategoryIds.flatMap(collectAllCategoryIds);
  }

  /**
   * Collects all categoryIds from a grouping
   * Considers CategoryType asset associations; other association types are ignored.
   * @param {string|number} groupingId - The grouping ID to collect from.
   * @returns {Array<string|number>} Flat array of categoriesIds
   */
  function collectCategoryIdsFromGrouping(groupingId) {
    const grouping = store.getters['groupings/all'][groupingId];
    if (!grouping) return [];

    return groupingCategoryTypeIds(grouping).flatMap(collectRootCategoryIdsFromType);
  }

  /**
   * Collect categoryIds from a resource which may be a category, category type, or grouping.
   * When both resourceId and resourceType are null/undefined, returns every category id in the store.
   * @param {string|number|null} resourceId - The ID of the resource, or null for all.
   * @param {string|null} resourceType - 'Category', 'CategoryType', 'Grouping', or null for all.
   * @returns {Array<string|number>} Flat array of categoriesIds
   */
  function collectCategoryIdsByResource(resourceId, resourceType) {
    if (resourceId == null && resourceType == null) {
      return store.getters['categories/all'].map(c => c.id);
    }
    if (resourceType === 'Category') {
      return collectAllCategoryIds(resourceId);
    } else if (resourceType === 'CategoryType') {
      return collectRootCategoryIdsFromType(resourceId);
    } else if (resourceType === 'Grouping') {
      return collectCategoryIdsFromGrouping(resourceId);
    }
    return [];
  }

  /**
   * Returns the number of unique flashcards across all categories reachable from a resource.
   * Flashcards appearing in multiple categories are counted only once.
   * For Grouping resources, considers CategoryType asset associations; other
   * association types are ignored.
   * @param {string|number} resourceId - The ID of the resource.
   * @param {string} resourceType - 'Category', 'CategoryType', or 'Grouping'.
   * @returns {number} The deduplicated flashcard count.
   */
  function countUniqueFlashcards(resourceId, resourceType) {
    const categoryIds = collectCategoryIdsByResource(resourceId, resourceType);
    const uniqueFlashcardIds = new Set();
    for (const categoryId of categoryIds) {
      for (const fid of store.getters['productAssociations/categoryFlashcards'](categoryId)) {
        uniqueFlashcardIds.add(fid);
      }
    }
    return uniqueFlashcardIds.size;
  }

  return {
    flashcardCounts,
    collectCategoryIdsByResource,
    countUniqueAnsweredFlashcards,
    countUniqueFlashcards,
    getFlashcardCounts,
    getUniqueLatestFlashcardOutcomes,
  };
}

function emptyCounts() {
  return {
    count: 0,            // total flashcards in the category
    green: 0,            // answered and marked mastered
    yellow: 0,           // answered and marked partially learned
    red: 0,              // answered and marked needs work
    taggedUnanswered: 0, // has the status of unanswered, but may in fact be answered
    taggedUnansweredButHasAnswer: 0, // tagged unanswered, but correct is true or false
    answered: 0,         // total with a correct/incorrect response
    bookmarked: 0,       // user-bookmarked flashcards
    correct: 0,          // answered correctly
    incorrect: 0,        // answered incorrectly
    unanswered: 0,       // no response recorded
    unansweredNoState: 0,   // no flashcard state record exists
    unansweredWithState: 0, // state record exists but no correct/incorrect response
    totalTime: 0,        // summed timeInSeconds across answered flashcards
    averageTime: null,   // totalTime / answered (null if none answered)
  };
}

function answered(state) {
  return state.correct === true || state.correct === false;
}

function mergeCounts(acc, other) {
  for (const key of Object.keys(acc)) {
    if (key === 'averageTime') continue;
    acc[key] += other[key];
  }
  acc.averageTime = acc.answered > 0 ? acc.totalTime / acc.answered : null;
  return acc;
}

function groupingCategoryTypeIds(grouping) {
  return grouping.assetAssociations
    .filter(aa => aa.assetBType === 'CategoryType')
    .map(aa => aa.assetBId);
}
